/**
 * WebhookFailuresPage — operator inbox for synchronous webhook-handler
 * failures.
 *
 * Converged onto the shared resource framework (#1417): the former 788-line
 * bespoke page (hand-wired QueryDataTable, an `__actions__` cell, a hand-rolled
 * BulkActionBar, a custom drawer + duplicate footers) is now `ResourcePage` +
 * `webhookFailureResource`. The resolve/replay/delete trio rides the rowActions
 * v2 pipeline AND the #1417 `detail.actions` footer from one source; bulk
 * resolve/delete are `resourceBulkActions`; the OPEN-count badge rides the
 * `listTitleAdornment` seam.
 *
 * This wrapper carries only what is NOT row-scoped: the cross-route
 * `WebhooksTabs` nav strip and the whole-history "Clear all" purge (which
 * deletes resolved history too, so it is not a resource action). The resource
 * is built IN-COMPONENT so its actions capture the page's mutation callbacks.
 */

import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button, ResourcePage } from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useBetterAuth } from '@hooks/useAuth';
import { WebhooksTabs } from '@features/webhooks';
import { WEBHOOK_FAILURES_WRITE_PERMISSION } from '@/services/webhook-failures';
import { usePurgeWebhookFailures, useWebhookFailuresCount } from '../hooks/useWebhookFailures';
import { useWebhookFailureResource } from './webhookFailureResource';

export default function WebhookFailuresPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { user } = useBetterAuth();
  const canWrite = (user?.permissions ?? []).includes(WEBHOOK_FAILURES_WRITE_PERMISSION);

  const purgeMutation = usePurgeWebhookFailures();

  // The GLOBAL open-failure count — still needed here for the purge confirm
  // copy (the whole-table purge clears resolved history too, so it names the
  // unresolved count for context rather than a delete total). The BADGE it also
  // used to drive moved onto the Failures tab in #2005; both read the same
  // cached `/count` query, so there is one number, not two.
  const totalOpen = useWebhookFailuresCount().data?.count ?? 0;

  const resource = useWebhookFailureResource();

  // "Clear all": wholesale purge of the ENTIRE failure log (resolved history
  // included). The confirm copy must not claim the inbox's open-only total as
  // the delete count.
  function handlePurge() {
    return confirm({
      title: t('webhookFailures.purge.confirmTitle'),
      message: t('webhookFailures.purge.confirmMessage', { count: totalOpen }),
      type: 'danger',
      confirmText: t('webhookFailures.purge.button'),
      confirmTestId: 'wf-purge-confirm',
      onConfirm: async () => {
        try {
          const result = await purgeMutation.mutateAsync({});
          toast.success(
            t('webhookFailures.purge.success.title'),
            t('webhookFailures.purge.success.summary', { count: result.deleted }),
          );
        } catch (err) {
          toast.error(
            t('webhookFailures.purge.error.title'),
            t('webhookFailures.purge.error.unknown', {
              detail: err instanceof Error ? err.message : String(err),
            }),
          );
        }
      },
    });
  }

  // Purge only, right-aligned in the PageHeader actions region. The backlog
  // BADGE moved onto the Failures tab in #2005: the page title now says
  // "Webhooks", and a number beside it would read as a count of webhooks. It
  // also belongs next to the word "Failures" wherever the operator is standing,
  // which the strip gives it on all three tabs.
  const headerActions = canWrite ? (
    <Button
      variant="outline"
      size="sm"
      onClick={handlePurge}
      disabled={purgeMutation.isPending}
      aria-busy={purgeMutation.isPending}
      className="text-destructive hover:text-destructive"
    >
      <Trash2 className="mr-1 h-3.5 w-3.5" />
      {t('webhookFailures.purge.button')}
    </Button>
  ) : undefined;

  return (
    <ResourcePage
      bundle={{
        ...resource,
        host: {
          ...resource.host,
          listTitle: 'webhooks.pageTitle',
          listTabs: () => <WebhooksTabs />,
        },
      }}
      headerActions={headerActions}
    />
  );
}
